import {
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  OnDestroy,
  Signal,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  IProductInCart,
  ShoppingCartService,
} from '../../../services/shopping-cart/shopping-cart.service';
import { ShoppingCartFormService } from '../../../services/shopping-cart/shopping-cart-form.service';
import {
  EEntityType,
  ERelationType,
  ICorporateDesign,
  IAdditionalEntryValue,
  ITariffAdditionalEntry,
  TariffProductDefinition,
  TariffProductGroup,
  TariffSelectionAttribute,
  TariffSelectionAttributeValue,
  TRAVEL_TYPES_WITH_TIME_PICKER,
} from '@web/dtos';
import { StringArrayToButtonOptionArrayPipe } from '../../../pipes/stringArrayToButtonOptionArray/string-array-to-button-option-array.pipe';
import { NgStyle } from '@angular/common';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';
import { ITicketDefinitionForm } from '../../../services/shopping-cart/shopping-cart-form.service';
import { ButtonGroupComponent } from '../form-inputs/button-group/button-group.component';
import { DateInputComponent } from '../form-inputs/date-input/date-input/date-input.component';
import { StaticFormFieldComponent } from '../form-inputs/static-form-field/static-form-field.component';
import { ButtonComponent } from '../../feedback/button/button.component';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { Subscription } from 'rxjs';
import { AdditionalEntriesFormComponent } from '../additonal-entries-form/additional-entries-form.component';

@Component({
  selector: 'aw-ticket-options-form',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    StringArrayToButtonOptionArrayPipe,
    NgStyle,
    ButtonGroupComponent,
    DateInputComponent,
    StaticFormFieldComponent,
    ButtonComponent,
    FilterTextsPipe,
    LanguagePipe,
    AdditionalEntriesFormComponent,
  ],
  templateUrl: './ticket-options-form.component.html',
})
export class TicketOptionsFormComponent implements OnDestroy {
  public readonly positionInCart: InputSignal<number> = input.required();
  public readonly productForm: InputSignal<FormGroup<ITicketDefinitionForm>> =
    input.required();
  public readonly productDefinition: InputSignal<TariffProductDefinition> =
    input.required();
  public readonly tariffProductGroup: InputSignal<TariffProductGroup> =
    input.required();
  public readonly listDates: Signal<string[] | null> = computed(() => {
    const list: string[] = this.productDefinition().validityOptions.dateList;
    return list && list.length > 0 ? list : null;
  });
  protected readonly shoppingCartService: ShoppingCartService =
    inject(ShoppingCartService);
  private readonly shoppingCartFormService: ShoppingCartFormService = inject(
    ShoppingCartFormService
  );
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  public showTimePicker: Signal<boolean> = computed(() => {
    return TRAVEL_TYPES_WITH_TIME_PICKER.includes(
      this.productDefinition().validityOptions.type
    );
  });
  protected readonly dateInputErrorRelationType: Signal<ERelationType> =
    computed(() =>
      this.showTimePicker()
        ? ERelationType.dateTimeInputError
        : ERelationType.dateInputError
    );

  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly ERelationType: typeof ERelationType = ERelationType;

  public readonly corporateDesign: Signal<ICorporateDesign> =
    this.corporateDesignService.corporateDesign;

  protected readonly isGroupTicket: Signal<boolean> = computed(() => {
    const maxNumberOfPeople =
      this.productForm().controls.product.value?.maxNumberOfPeople ?? 1;

    return maxNumberOfPeople > 1;
  });

  protected readonly selectionAttributes: Signal<TariffSelectionAttribute[]> =
    computed(() => {
      return this.tariffProductGroup().selectionAttributes.filter(attr => {
        return attr.name.toLowerCase() !== 'name';
      });
    });
  protected readonly productSortOptions: Signal<TariffSelectionAttribute[]> =
    computed(() => {
      return this.tariffProductGroup().selectionAttributes.filter(attr => {
        return attr.name.toLowerCase() === 'name';
      });
    });

  private readonly subscription: Subscription = new Subscription();

  constructor() {
    effect((): void => {
      if (this.tariffProductGroup()) {
        this.pickProductFromForm();
        this.shoppingCartService.adjustNumberOfTicketOwnerForms(
          this.positionInCart(),
          this.productForm().controls.amount.value
        );
      }
    });
    effect(() => {
      this.subscription.add(
        this.productForm().valueChanges.subscribe(value => {
          this.shoppingCartService.productForms.updateValueAndValidity();
        })
      );
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  protected increaseAmount(
    productForm: FormGroup<ITicketDefinitionForm>
  ): void {
    if (
      productForm!.controls.amount.value < this.shoppingCartService.maxAmount
    ) {
      productForm!.controls.amount.setValue(
        productForm!.controls.amount.value + 1
      );
      this.shoppingCartService.adjustNumberOfTicketOwnerForms(
        this.positionInCart(),
        productForm!.controls.amount.value
      );
    }
    this.shoppingCartService.productForms.updateValueAndValidity();
  }
  protected decreaseAmount(
    productForm: FormGroup<ITicketDefinitionForm>
  ): void {
    if (
      productForm!.controls.amount.value > this.shoppingCartService.minAmount
    ) {
      productForm?.controls.amount.setValue(
        productForm?.controls.amount.value - 1
      );
      this.shoppingCartService.adjustNumberOfTicketOwnerForms(
        this.positionInCart(),
        productForm!.controls.amount.value
      );
    }
    this.shoppingCartService.productForms.updateValueAndValidity();
  }

  protected changeAmountOfPeople(
    productForm: FormGroup<ITicketDefinitionForm>,
    change: -1 | 1
  ): void {
    const minimumNumberOfPeople: number =
      productForm.controls.product.value?.minNumberOfPeople ??
      productForm.controls.product.value?.numberOfPeople ??
      1;
    const maximumNumberOfPeople: number =
      productForm.controls.product.value?.maxNumberOfPeople ??
      productForm.controls.amountOfPeople.value;
    const amountOfPeople: number = productForm.controls.amountOfPeople.value;
    const newPeopleAmount: number = amountOfPeople + change;

    if (
      newPeopleAmount < minimumNumberOfPeople ||
      newPeopleAmount > maximumNumberOfPeople
    ) {
      return;
    }

    const sourceProduct: IProductInCart | undefined =
      this.shoppingCartService.findProductByProductForm(productForm);

    if (sourceProduct) {
      this.shoppingCartService.moveOneTicketToAmountOfPeople(
        sourceProduct,
        newPeopleAmount
      );
    } else {
      productForm.controls.amountOfPeople.setValue(newPeopleAmount);
      this.shoppingCartService.adjustNumberOfCompanionForms(
        this.positionInCart(),
        newPeopleAmount
      );
    }

    this.shoppingCartService.productForms.updateValueAndValidity();
  }

  protected pickProduct(
    selectionAttributes: TariffSelectionAttributeValue[]
  ): void {
    const currentProduct: TariffProductDefinition | undefined =
      this.productForm().controls.product.value;
    const product: TariffProductDefinition | undefined =
      this.tariffProductGroup().productDefinitions.find(
        (ticketDefinition: TariffProductDefinition) => {
          const match: boolean = selectionAttributes
            .map(criteria => {
              return !!ticketDefinition.keyElements.find(
                (el: { name: string; value: TariffSelectionAttributeValue }) =>
                  el.value === criteria
              );
            })
            .reduce((acc: boolean, curr: boolean) => acc && curr, true);
          if (match) {
            return ticketDefinition;
          }
          return undefined;
        }
      );
    const isSameProduct: boolean =
      currentProduct?.versionNumber === product?.versionNumber &&
      JSON.stringify(currentProduct?.keyElements) ===
        JSON.stringify(product?.keyElements);
    const existingAdditionalEntries: IAdditionalEntryValue[] = isSameProduct
      ? this.productForm().controls.additionalEntries.getRawValue()
      : [];
    this.productForm().controls.product.setValue(product);
    this.shoppingCartFormService.updateAdditionalEntries(
      this.productForm(),
      product,
      existingAdditionalEntries
    );
    this.shoppingCartFormService.updateAmountOfPeople(
      this.productForm(),
      product,
      isSameProduct
    );
    this.shoppingCartService.adjustNumberOfCompanionForms(
      this.positionInCart(),
      this.productForm().controls.amountOfPeople.value
    );
    this.shoppingCartService.productForms.updateValueAndValidity();
  }

  protected pickProductFromForm(): void {
    this.pickProduct([
      ...this.productForm().controls.selectionAttributes.getRawValue(),
      ...this.productForm().controls.productSortOptions.getRawValue(),
    ]);
  }

  public listDatesToDate(listDates: string[] | null): Date[] | null {
    if (!listDates) {
      return null;
    }
    return listDates.map(date => new Date(date));
  }
  public getAdditionalEntries(): ITariffAdditionalEntry[] {
    return (
      this.productForm().controls.product.value?.sort.additionalEntries ?? []
    );
  }
}
